import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FeatureFlagUI, FlagFormPayload } from 'src/app/domains/feature-flags/models/feature-flags.model';
import { FeatureFlag } from 'voxloud-types/dist/models';

interface FlagFormGroup {
  key: FormControl<string>;
  description: FormControl<string>;
  is_enabled: FormControl<boolean>;
}

const FLAG_KEY_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

@Component({
  selector: 'gdp-add-edit-flag-form',
  templateUrl: './add-edit-flag-form.component.html',
  styleUrls: ['./add-edit-flag-form.component.scss']
})
export class AddEditFlagFormComponent implements OnInit {
  private readonly fb: FormBuilder = inject(FormBuilder);
  @Output() onSubmit: EventEmitter<FlagFormPayload> = new EventEmitter<FlagFormPayload>();
  @Input() title: string = '';
  @Input() flag: FeatureFlagUI;
  @Input() flags: FeatureFlag[] = [];

  public form: FormGroup<FlagFormGroup>;
  public _loading: boolean = false;
  @Input() set loading(v: boolean) {
    this._loading = v;
  }

  ngOnInit(): void {
    this.initForm();
  }

  public submit(): void {
    if (!this.canSubmit) {
      return;
    }

    const { key, description, is_enabled } = this.form.getRawValue();

    this.onSubmit.emit({
      key,
      description,
      is_enabled,
    });
  }

  public isFieldValid(name: keyof FlagFormGroup): boolean {
    const control = this.form.get(name);
    return !!control?.invalid && !!control?.dirty;
  }

  public get canSubmit(): boolean {
    return this.form.valid && this.form.dirty;
  }

  private initForm(): void {
    const existingKeys = new Set(this.flags.map(f => f.key));
    const originalKey = this.flag?.key;
    this.form = this.fb.group({
      key: new FormControl({ value: this.flag.key, disabled: !!this.flag.isEditing }, {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(FLAG_KEY_PATTERN),
          (control) => {
            const value = control.value;
            if (!value || value === originalKey) {
              return null;
            }
            return existingKeys.has(value) ? { exists: true } : null;
          },
        ],
      }),
      description: new FormControl(this.flag.description, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      is_enabled: new FormControl(this.flag.is_enabled, {
        nonNullable: true,
      }),
    });
  }
}